/**
 * Hyperliquid Market Data Adapter
 *
 * Implements PerpsMarketDataAdapter by wrapping HyperliquidInfoClient (REST)
 * and HLWsPool (WebSocket). Processing functions are extracted from the
 * HL hooks as pure, testable module-level functions.
 *
 * Supports multi-dex: fetches main HL perps + configured HIP-3 dexes in
 * parallel, merging into a unified market list with dex tags.
 *
 * REST methods: fetchOrderBook, fetchRecentTrades, fetchAllTickers, etc.
 * WS methods: subscribeOrderBook, subscribeRecentTrades, etc. — each returns
 * an unsubscribe function.
 */

import { HyperliquidInfoClient } from "../client";
import type {
  HLOrderbookLevel,
  HLAssetCtx,
  HLMeta,
  HLRecentTrade,
  HLWsTrade,
  HLWsMessage,
  HLWsCandleMsg,
} from "../client";
import { getHLWsPool } from "../browser/ws-pool";
import type {
  PerpsOrderBook,
  PerpsOrderBookLevel,
  PerpsTrade,
  PerpsMarketListItem,
  PerpsMarketTicker,
  PerpsMarkPrice,
} from "../types/market";
import type { ChartCandle, ChartTimeframe } from "../types/chart";
import type { PerpsMarketDataAdapter } from "./types";

// ============================================================================
// Constants
// ============================================================================

/** Milliseconds per candle for each timeframe */
const TIMEFRAME_MS: Record<ChartTimeframe, number> = {
  "1m": 60_000,
  "5m": 5 * 60_000,
  "15m": 15 * 60_000,
  "1h": 60 * 60_000,
  "4h": 4 * 60 * 60_000,
  "1d": 24 * 60 * 60_000,
};

const MAX_TRADES = 50;

// ============================================================================
// Symbol Helpers
// ============================================================================

/**
 * Convert a canonical symbol to HL coin name.
 * Handles both main dex and HIP-3 dex-prefixed symbols.
 *
 * "BTCUSDC" → "BTC"           (main dex)
 * "xyz:TSLAUSDC" → "xyz:TSLA" (HIP-3)
 */
function toHLCoin(symbol: string): string {
  const colonIdx = symbol.indexOf(":");
  if (colonIdx >= 0) {
    const dex = symbol.slice(0, colonIdx);
    const bare = symbol.slice(colonIdx + 1).replace(/USD[TC]$/i, "");
    return `${dex}:${bare}`;
  }
  return symbol.replace(/USD[TC]$/i, "");
}

/**
 * Extract the dex name from a canonical symbol.
 * "xyz:TSLAUSDC" → "xyz", "BTCUSDC" → undefined
 */
function extractDex(symbol: string): string | undefined {
  const colonIdx = symbol.indexOf(":");
  return colonIdx >= 0 ? symbol.slice(0, colonIdx) : undefined;
}

/**
 * Strip dex prefix from HIP-3 asset name.
 * "birb:PENGU" → "PENGU", "BTC" → "BTC"
 */
function stripDexPrefix(name: string): string {
  const colonIdx = name.indexOf(":");
  return colonIdx >= 0 ? name.slice(colonIdx + 1) : name;
}

// ============================================================================
// Processing Functions (pure, extracted from hooks for testability)
// ============================================================================

/**
 * Process raw HL order book levels into normalized PerpsOrderBookLevel[].
 * @param rawLevels - Raw levels from HL API
 * @param ascending - true for bids (highest first), false for asks (lowest first)
 */
export function processHLLevels(
  rawLevels: HLOrderbookLevel[],
  ascending: boolean,
): PerpsOrderBookLevel[] {
  const levels: PerpsOrderBookLevel[] = [];
  let cumulative = 0;

  const sorted = [...rawLevels].sort((a, b) => {
    const priceA = parseFloat(a.px);
    const priceB = parseFloat(b.px);
    return ascending ? priceB - priceA : priceA - priceB;
  });

  for (const level of sorted) {
    const price = parseFloat(level.px);
    const quantity = parseFloat(level.sz);
    if (quantity <= 0) continue;
    cumulative += quantity;
    levels.push({ price, quantity, total: cumulative });
  }

  return levels;
}

/**
 * Process raw HL bids/asks into a complete PerpsOrderBook.
 */
export function processHLOrderBook(
  bids: HLOrderbookLevel[],
  asks: HLOrderbookLevel[],
): PerpsOrderBook {
  const processedBids = processHLLevels(bids, true);
  const processedAsks = processHLLevels(asks, false);

  const bestBid = processedBids[0]?.price ?? 0;
  const bestAsk = processedAsks[0]?.price ?? 0;
  const spread = bestAsk - bestBid;
  const midPrice = (bestAsk + bestBid) / 2;
  const spreadPercent = midPrice > 0 ? (spread / midPrice) * 100 : 0;

  const maxBidTotal = processedBids[processedBids.length - 1]?.total ?? 0;
  const maxAskTotal = processedAsks[processedAsks.length - 1]?.total ?? 0;
  const maxTotal = Math.max(maxBidTotal, maxAskTotal);

  return {
    bids: processedBids,
    asks: processedAsks,
    spread,
    spreadPercent,
    maxTotal,
  };
}

/**
 * Convert HL asset context to a PerpsMarketTicker.
 * @param coin - Base asset name (e.g., "BTC")
 * @param ctx - HL asset context
 * @param dayHL - Optional 24h high/low from candle data
 */
export function assetCtxToTicker(
  coin: string,
  ctx: HLAssetCtx,
  dayHL?: { highPrice: number; lowPrice: number },
): PerpsMarketTicker {
  const midPx = ctx.midPx ? parseFloat(ctx.midPx) : parseFloat(ctx.markPx);
  const prevDayPx = parseFloat(ctx.prevDayPx);
  const priceChange = midPx - prevDayPx;
  const priceChangePercent =
    prevDayPx > 0 ? (priceChange / prevDayPx) * 100 : 0;
  const dayNtlVlm = parseFloat(ctx.dayNtlVlm);

  return {
    symbol: coin,
    lastPrice: midPx,
    priceChange,
    priceChangePercent,
    highPrice: dayHL?.highPrice ?? 0,
    lowPrice: dayHL?.lowPrice ?? 0,
    volume: dayNtlVlm,
    quoteVolume: dayNtlVlm,
  };
}

/**
 * Convert HL asset context to PerpsMarkPrice.
 */
export function assetCtxToMarkPrice(ctx: HLAssetCtx): PerpsMarkPrice {
  return {
    markPrice: parseFloat(ctx.markPx),
    indexPrice: parseFloat(ctx.oraclePx),
    fundingRate: parseFloat(ctx.funding),
    nextFundingTime: estimateNextFundingTime(),
    openInterest: parseFloat(ctx.openInterest),
  };
}

/**
 * Estimate next funding time (HL settles hourly).
 */
export function estimateNextFundingTime(): number {
  const now = Date.now();
  const msPerHour = 3600_000;
  return Math.ceil(now / msPerHour) * msPerHour;
}

/**
 * Convert a single HL trade (REST or WS) to PerpsTrade.
 */
export function processHLTrade(trade: HLRecentTrade | HLWsTrade): PerpsTrade {
  return {
    id: trade.tid,
    price: parseFloat(trade.px),
    quantity: parseFloat(trade.sz),
    time: trade.time,
    isBuy: trade.side === "B",
  };
}

/**
 * Build a market list from one dex's meta + contexts.
 * @param dexName - undefined for main perps, "xyz" for HIP-3 dexes
 */
export function buildHLMarketList(
  meta: HLMeta,
  mids: Record<string, string>,
  ctxs: HLAssetCtx[],
  favorites: string[],
  categories?: Record<string, string>,
  dexName?: string,
): PerpsMarketListItem[] {
  return meta.universe
    .map((asset, index) => {
      const ctx = ctxs[index];
      // allMids only has main dex prices; for HIP-3 assets use ctx.midPx/markPx
      const midStr = mids[asset.name];
      const midPrice = midStr
        ? parseFloat(midStr)
        : ctx?.midPx
          ? parseFloat(ctx.midPx)
          : ctx
            ? parseFloat(ctx.markPx)
            : 0;

      let priceChangePercent = 0;
      if (ctx) {
        const prevDayPx = parseFloat(ctx.prevDayPx);
        if (prevDayPx > 0) {
          priceChangePercent = ((midPrice - prevDayPx) / prevDayPx) * 100;
        }
      }

      const volume24h = ctx ? parseFloat(ctx.dayNtlVlm) : 0;
      const displayName = stripDexPrefix(asset.name);

      // For HIP-3: symbol includes dex prefix for routing (e.g. "xyz:TSLAUSDC")
      // For main dex: plain symbol (e.g. "BTCUSDC")
      const symbol = dexName
        ? `${dexName}:${displayName}USDC`
        : `${displayName}USDC`;

      return {
        symbol,
        baseAsset: displayName,
        quoteAsset: "USDC",
        lastPrice: midPrice,
        priceChangePercent,
        volume24h,
        isFavorite: favorites.includes(symbol),
        category: categories?.[displayName],
        dexName,
      };
    })
    .sort((a, b) => b.volume24h - a.volume24h);
}

/**
 * Convert a WS candle message data payload to ChartCandle.
 */
export function wsCandleToChart(data: HLWsCandleMsg["data"]): ChartCandle {
  return {
    time: Math.floor(data.t / 1000),
    open: parseFloat(data.o),
    high: parseFloat(data.h),
    low: parseFloat(data.l),
    close: parseFloat(data.c),
    volume: parseFloat(data.v),
  };
}

// ============================================================================
// Adapter Class
// ============================================================================

export class HyperliquidMarketData implements PerpsMarketDataAdapter {
  private restClient: HyperliquidInfoClient;
  private testnet: boolean;
  private dexNames: string[];

  // Cache metaAndAssetCtxs to avoid hammering the HL API.
  // Keyed by dex name ("__main__" for main dex).
  private metaCache = new Map<
    string,
    { data: [HLMeta, HLAssetCtx[]]; ts: number }
  >();
  private static META_CACHE_TTL = 10_000; // 10s TTL

  constructor(
    testnet = false,
    dexNames?: string[],
    sharedClient?: HyperliquidInfoClient,
  ) {
    this.restClient =
      sharedClient ?? new HyperliquidInfoClient({ testnet, dexNames });
    this.testnet = testnet;
    this.dexNames = dexNames ?? [];
  }

  /**
   * Cached wrapper around getMetaAndAssetCtxs.
   * Returns cached data if within TTL, otherwise fetches fresh.
   */
  private async getCachedMetaAndCtxs(
    dex?: string,
  ): Promise<[HLMeta, HLAssetCtx[]]> {
    const key = dex ?? "__main__";
    const cached = this.metaCache.get(key);
    if (
      cached &&
      Date.now() - cached.ts < HyperliquidMarketData.META_CACHE_TTL
    ) {
      return cached.data;
    }
    try {
      const data = (await this.restClient.getMetaAndAssetCtxs(dex)) as [
        HLMeta,
        HLAssetCtx[],
      ];
      this.metaCache.set(key, { data, ts: Date.now() });
      return data;
    } catch (e) {
      // Transient HL outages shouldn't nuke the entire ticker list — if we
      // have ANY cached data (even past TTL), serve it so the UI keeps
      // rendering. Only re-throw when we have literally nothing.
      if (cached) {
        // eslint-disable-next-line no-console
        console.warn(
          `[HL] getMetaAndAssetCtxs(${key}) failed; serving stale cache (${Math.round((Date.now() - cached.ts) / 1000)}s old)`,
          e,
        );
        return cached.data;
      }
      throw e;
    }
  }

  // --------------------------------------------------------------------------
  // REST fetchers
  // --------------------------------------------------------------------------

  async fetchOrderBook(
    symbol: string,
    _levels?: number,
  ): Promise<PerpsOrderBook> {
    const coin = toHLCoin(symbol);
    const book = await this.restClient.getL2Book(coin);
    return processHLOrderBook(book.levels[0], book.levels[1]);
  }

  async fetchRecentTrades(symbol: string): Promise<PerpsTrade[]> {
    const coin = toHLCoin(symbol);
    const raw = await this.restClient.getRecentTrades(coin);
    const processed = raw.map(processHLTrade);
    processed.reverse();
    if (processed.length > MAX_TRADES) {
      processed.length = MAX_TRADES;
    }
    return processed;
  }

  async fetchAllTickers(favorites: string[]): Promise<PerpsMarketListItem[]> {
    // Parallel fetch: main dex + each HIP-3 dex + categories (all cached)
    const promises: Promise<unknown>[] = [
      this.getCachedMetaAndCtxs(), // [0] main dex
      this.restClient.getAllMids(), // [1] main dex prices
    ];

    // Add HIP-3 dex fetches
    for (const dex of this.dexNames) {
      promises.push(this.getCachedMetaAndCtxs(dex));
    }

    // Add perpCategories (one call covers all HIP-3 dexes)
    const hasDexes = this.dexNames.length > 0;
    if (hasDexes) {
      promises.push(this.restClient.getPerpCategories());
    }

    const results = await Promise.all(promises);

    // Unpack main dex results
    const [mainMeta, mainCtxs] = results[0] as [HLMeta, HLAssetCtx[]];
    const mids = results[1] as Record<string, string>;

    // Build main dex market list
    const allMarkets = buildHLMarketList(
      mainMeta,
      mids,
      mainCtxs,
      favorites,
      undefined,
      undefined,
    );

    // Build HIP-3 dex market lists
    if (hasDexes) {
      const allCategories = results[2 + this.dexNames.length] as [
        string,
        string,
      ][];
      const categoryMap = buildCategoryMap(allCategories);

      for (let i = 0; i < this.dexNames.length; i++) {
        const dex = this.dexNames[i]!;
        const [dexMeta, dexCtxs] = results[2 + i] as [HLMeta, HLAssetCtx[]];
        const dexCategories = categoryMap[dex];
        const dexMarkets = buildHLMarketList(
          dexMeta,
          {},
          dexCtxs,
          favorites,
          dexCategories,
          dex,
        );
        allMarkets.push(...dexMarkets);
      }

      // Re-sort merged list by volume
      allMarkets.sort((a, b) => b.volume24h - a.volume24h);
    }

    return allMarkets;
  }

  async fetchMarketTicker(symbol: string): Promise<PerpsMarketTicker> {
    const coin = toHLCoin(symbol);
    const dex = extractDex(symbol);
    const [meta, ctxs] = await this.getCachedMetaAndCtxs(dex);
    const idx = meta.universe.findIndex((a) => a.name === coin);
    if (idx === -1) throw new Error(`Asset ${coin} not found`);

    // Fetch 24h high/low from candle data
    let dayHL = { highPrice: 0, lowPrice: 0 };
    try {
      const now = Date.now();
      const candles = await this.restClient.getCandleSnapshot(
        coin,
        "1h",
        now - 86400000,
        now,
      );
      if (candles.length > 0) {
        let high = -Infinity;
        let low = Infinity;
        for (const c of candles) {
          const h = parseFloat(c.h);
          const l = parseFloat(c.l);
          if (h > high) high = h;
          if (l < low) low = l;
        }
        dayHL = { highPrice: high, lowPrice: low };
      }
    } catch {
      // Silently fail — keep zero high/low
    }

    return assetCtxToTicker(coin, ctxs[idx]!, dayHL);
  }

  async fetchMarkPrice(symbol: string): Promise<PerpsMarkPrice> {
    const coin = toHLCoin(symbol);
    const dex = extractDex(symbol);
    const [meta, ctxs] = await this.getCachedMetaAndCtxs(dex);
    const idx = meta.universe.findIndex((a) => a.name === coin);
    if (idx === -1) throw new Error(`Asset ${coin} not found`);
    return assetCtxToMarkPrice(ctxs[idx]!);
  }

  async fetchChartData(
    symbol: string,
    interval: ChartTimeframe,
    limit = 500,
  ): Promise<ChartCandle[]> {
    const coin = toHLCoin(symbol);
    const now = Date.now();
    const msPerCandle = TIMEFRAME_MS[interval];
    const startTime = now - msPerCandle * limit;
    const hlCandles = await this.restClient.getCandleSnapshot(
      coin,
      interval,
      startTime,
      now,
    );
    return hlCandles.map((c) => ({
      time: Math.floor(c.t / 1000),
      open: parseFloat(c.o),
      high: parseFloat(c.h),
      low: parseFloat(c.l),
      close: parseFloat(c.c),
      volume: parseFloat(c.v),
    }));
  }

  // --------------------------------------------------------------------------
  // WebSocket subscriptions (each returns an unsubscribe function)
  // --------------------------------------------------------------------------

  subscribeOrderBook(
    symbol: string,
    onData: (data: PerpsOrderBook) => void,
  ): () => void {
    const coin = toHLCoin(symbol);
    const pool = getHLWsPool(this.testnet);
    return pool.subscribe({ type: "l2Book", coin }, (msg: HLWsMessage) => {
      if (msg.channel !== "l2Book") return;
      onData(processHLOrderBook(msg.data.levels[0], msg.data.levels[1]));
    });
  }

  subscribeRecentTrades(
    symbol: string,
    onData: (trades: PerpsTrade[]) => void,
  ): () => void {
    const coin = toHLCoin(symbol);
    const pool = getHLWsPool(this.testnet);
    return pool.subscribe({ type: "trades", coin }, (msg: HLWsMessage) => {
      if (msg.channel !== "trades") return;
      const trades = msg.data
        .filter((t) => t.coin === coin)
        .map(processHLTrade);
      if (trades.length > 0) onData(trades);
    });
  }

  subscribeMarketTicker(
    symbol: string,
    onData: (data: PerpsMarketTicker) => void,
  ): () => void {
    const coin = toHLCoin(symbol);
    const pool = getHLWsPool(this.testnet);
    return pool.subscribe(
      { type: "activeAssetCtx", coin },
      (msg: HLWsMessage) => {
        if (msg.channel !== "activeAssetCtx") return;
        onData(assetCtxToTicker(msg.data.coin, msg.data.ctx));
      },
    );
  }

  subscribeMarkPrice(
    symbol: string,
    onData: (data: PerpsMarkPrice) => void,
  ): () => void {
    const coin = toHLCoin(symbol);
    const pool = getHLWsPool(this.testnet);
    return pool.subscribe(
      { type: "activeAssetCtx", coin },
      (msg: HLWsMessage) => {
        if (msg.channel !== "activeAssetCtx") return;
        onData(assetCtxToMarkPrice(msg.data.ctx));
      },
    );
  }

  subscribeCandle(
    symbol: string,
    interval: ChartTimeframe,
    onData: (candle: ChartCandle) => void,
  ): () => void {
    const coin = toHLCoin(symbol);
    const pool = getHLWsPool(this.testnet);
    return pool.subscribe(
      { type: "candle", coin, interval },
      (msg: HLWsMessage) => {
        if (msg.channel !== "candle") return;
        onData(wsCandleToChart((msg as HLWsCandleMsg).data));
      },
    );
  }

  subscribeAllMids(onData: (mids: Record<string, string>) => void): () => void {
    const pool = getHLWsPool(this.testnet);
    return pool.subscribe({ type: "allMids" }, (msg: HLWsMessage) => {
      if (msg.channel !== "allMids") return;
      onData(msg.data.mids);
    });
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build per-dex category maps from the perpCategories response.
 * Input: [["xyz:TSLA", "stocks"], ["cash:AAPL", "stocks"], ...]
 * Output: { "xyz": { "TSLA": "stocks" }, "cash": { "AAPL": "stocks" } }
 */
function buildCategoryMap(
  tuples: [string, string][],
): Record<string, Record<string, string>> {
  const map: Record<string, Record<string, string>> = {};
  for (const [coin, cat] of tuples) {
    const colonIdx = coin.indexOf(":");
    if (colonIdx < 0) continue;
    const dex = coin.slice(0, colonIdx);
    const bare = coin.slice(colonIdx + 1);
    if (!map[dex]) map[dex] = {};
    map[dex][bare] = cat;
  }
  return map;
}
